/**
 * Epic 9: System Settings & Master Data Types
 */
// System Settings
/**
 * Gepinde system_settings category/key-identifiers (ARCHITECTURE-SPINE.md
 * "Gepinde identifiers" — Epic 4/5/6). Eén gedeelde constante, geen
 * los-gestrooide literals in routes/services.
 */
export const SYSTEM_SETTING_KEYS = {
    coreTasks: {
        category: 'coreTasks',
        active: 'core_tasks_active',
        impactReportReviewedBy: 'impact_report_reviewed_by',
        impactReportReviewedAt: 'impact_report_reviewed_at',
        // Epic 6, Story 6.3 — "Handleiding gepubliceerd"-checkbox in de
        // Migratie-tab. Niet in de architectuur-spine "Gepinde identifiers"
        // gelijst (die noemt alleen de drie sleutels hierboven) omdat geen
        // andere story/AD hierop leunt — puur Story 6.3-interne staat, maar via
        // deze gedeelde constante gecentraliseerd i.p.v. losse literals
        // (zelfde motivatie als de drie gepinde sleutels).
        manualPublished: 'core_tasks_manual_published',
        manualPublishedBy: 'core_tasks_manual_published_by',
        manualPublishedAt: 'core_tasks_manual_published_at',
    },
    verlof: {
        category: 'verlof',
        /**
         * Aantal vakantiedagen per jaar bij een voltijds dienstverband
         * (5-daagse werkweek). Organisatiebreed, geen uitzondering per
         * medewerker — besluit Friso 2026-07-29.
         *
         * Hieruit volgt het jaarrecht in uren, gesplitst over wettelijk en
         * bovenwettelijk verlof: wettelijk is bij wet 4× de weekuren
         * (art. 7:634 BW), het restant is bovenwettelijk. Verhoog je deze
         * instelling, dan groeit dus ALLEEN het bovenwettelijke deel.
         */
        vakantiedagenPerJaar: 'vakantiedagen_per_jaar',
        /**
         * Het bedrijfsbrede pauzevenster (Epic 1 — Verlof correctie/kort verlof,
         * Story 1.1, AD-15). Eén waarde voor de hele organisatie: geen pauze per
         * medewerker, per afdeling of per rooster (FR-3; addendum §3 verworpen
         * op 2026-08-04). Opgeslagen als één JSON-gecodeerde string in de vorm
         * `"HH:MM-HH:MM"` (zie `PauzeVenster`/`parsePauzeVenster` hieronder).
         *
         * AD-15 (hard): de gedeelde urenfunctie (`workabilityResolver.ts`) mag
         * deze instelling NOOIT zelf opzoeken — ze krijgt het venster als
         * argument. Alleen backend-services buiten `packages/shared` lezen deze
         * sleutel.
         */
        pauzeVenster: 'pauze_venster',
        /**
         * Story 2.3 (Epic 2, Verlof correctie): tijdstip en uitkomst van de
         * laatst uitgevoerde "kenmerk terugzoeken over de hele historie"-actie
         * (AC-3). Geen nieuwe tabel, geen migratie — zelfde `system_settings`-
         * patroon als de twee sleutels hierboven. Waarde is een JSON-object
         * `{ gekoppeld, nietGekoppeld, uitgevoerdOp }`.
         */
        kenmerkKoppelactieLaatst: 'kenmerk_koppelactie_laatst',
        /**
         * Story 3.1 (Epic 3, Verlof correctie, AD-22/AC-12): the first last-leave-day
         * for which the portal asks the employee to confirm their hours. Leave that
         * ended BEFORE this date is out of scope: no reminder card, and never the
         * *Niet bevestigd* badge — nobody was ever asked, so nothing was missed.
         *
         * Data, not schema: one row in `system_settings`, set by hand before the
         * rollout (story 3.1, taak 8). Without that row the reader falls back to
         * {@link DEFAULT_BEVESTIGING_INGANGSDATUM}; it never throws, because a
         * missing setting must not take the leave screen down.
         *
         * The name is binding — the row is created by hand, so the row and the code
         * have to carry the same spelling.
         */
        bevestigingIngangsdatum: 'bevestiging_ingangsdatum',
        /**
         * Story 5.4 (Verlofsoorten, FR-19/AD-26/AD-40): de dag vanaf wanneer de
         * nieuwe verlofsoorten gelden. Een aanvraag met een STARTdatum op of ná
         * deze dag valt onder de nieuwe regels; alles daarvóór gedraagt zich
         * precies zoals de portal zich altijd al gedroeg — geen soort, geen
         * HR-stap, geen plek in de maandteller.
         *
         * Data, geen schema: één rij in `system_settings`, met de hand gezet vóór
         * het image. Deze sleutel voegt niets toe aan migratie 0035.
         *
         * **Ontbreekt de rij, dan staat de schakelaar UIT** — anders dan
         * {@link DEFAULT_BEVESTIGING_INGANGSDATUM} en
         * {@link DEFAULT_PAUZE_VENSTER} is er hier bewust GEEN standaardwaarde.
         * Een standaarddatum in het verleden zou het hele werk stilzwijgend
         * aanzetten op een omgeving zonder testvangnet (KE-8).
         *
         * De naam is bindend — de rij wordt met de hand aangemaakt, dus de rij en
         * de code moeten dezelfde spelling dragen.
         */
        verlofsoortIngangsdatum: 'verlofsoort_ingangsdatum',
    },
};
/**
 * Standaard wanneer `verlof/vakantiedagen_per_jaar` nog niet is ingesteld.
 * Een ontbrekende rij mag de verlofschermen niet stukmaken.
 */
export const DEFAULT_VAKANTIEDAGEN_PER_JAAR = 25;
/**
 * Standaard wanneer `verlof/bevestiging_ingangsdatum` nog niet is ingesteld of
 * onleesbaar is (Story 3.1, AC-12).
 *
 * Bewust een datum en niet "het begin der tijden": zou de terugval alles
 * omvatten, dan krijgt iedere medewerker bij een vergeten instellingsrij een
 * herinnering voor zijn hele verlofhistorie — precies wat AD-22 wil voorkomen.
 */
export const DEFAULT_BEVESTIGING_INGANGSDATUM = '2026-01-01';
/**
 * Standaard pauzevenster wanneer `verlof/pauze_venster` nog niet is
 * ingesteld, onleesbaar is, of een ongeldige waarde bevat (FR-3, AC-1/AC-4
 * van Story 1.1). Een corrupte instelling mag de urenberekening niet
 * blokkeren — terugvallen op deze waarde en doorrekenen.
 */
export const DEFAULT_PAUZE_VENSTER = '12:30-13:00';
const PAUZE_VENSTER_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/;
/**
 * Parseert een `"HH:MM-HH:MM"`-string naar een `PauzeVenster`. Geeft `null`
 * terug bij elke afwijkende vorm (leeg, geen streepje, ongeldige tijd) —
 * de aanroeper is verantwoordelijk voor de terugval op
 * `DEFAULT_PAUZE_VENSTER` (zie `getPauzeVenster` in de backend). Deze
 * functie toetst uitsluitend de VORM, niet of de eindtijd na de begintijd
 * ligt of het venster niet nul minuten is — dat doet `pauzeVensterSchema`
 * (`schemas/settings.ts`) op het schrijfpad.
 */
export function parsePauzeVenster(raw) {
    const match = PAUZE_VENSTER_PATTERN.exec(raw);
    if (!match)
        return null;
    return { start: `${match[1]}:${match[2]}`, end: `${match[3]}:${match[4]}` };
}
/** Serialiseert een `PauzeVenster` terug naar de opgeslagen `"HH:MM-HH:MM"`-vorm. */
export function serializePauzeVenster(venster) {
    return `${venster.start}-${venster.end}`;
}
//# sourceMappingURL=settings.js.map